import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import ContentEditable from 'react-contenteditable';
import { useSelector } from 'react-redux';
import { EventsState } from '../../../store/types';
import { ColorKeys, TextOverrides, } from '../../../types/working-model';
import { useRefCallback } from '../../../utils/use-ref-callback';
import EditSectionColorsModal, { EContentColorOverrideNames } from '../../admin/create/session/editor/moduleControls/edit-section-colors-modal';
import { parseColor } from '../../admin/document-head';
import Icon, { COLORS, ICONS } from '../icon';
import './editable-text.scss';

interface Props {
	value: string;
	onChange: (value: string) => void; //handles text changes
	style?: any;
	className?: string;

	tagType?: "h1" | "h2" | "h3" | "h4" | "h5" | "p";//creates the specified tag and removes markdown, more tag types can be added

	elementName?: EContentColorOverrideNames; //name of the element being edited (description/title), used in the color editor modal

	//Your onOverrideChange func should update the styling_override attribute of the module you're editing
	//and include pathing to the specific elemnt you're editing
	//e.g.
	// {
	// 	...module,
	// 	styling_overrides: {
	// 		...module.styling_overrides,
	// 		[your element e.g "title"]: override
	// 	}
	// }
	onOverrideChange?: (override: TextOverrides) => void;
	// that path is also what you would pass in as the initial textOverrides
	colorOverride?: string;

	//include these defaults if you want to give the user the option of editing an override without setting one before hand. 
	//The default should represet what the template would be (title is "h1"/"h2" and "headingTextcolor", most things are left aligned etc.)
	defaultColor?: ColorKeys;
	disabled?: boolean;
}

function convertFromHtml(html: string): string {
	const tempCont = document.createElement('div');
	tempCont.innerHTML = html;
	return tempCont.innerText;
}

const EditableTextSlim: React.FC<Props> = (props) => {
	const {
		CreateEventReducer: {
			workingEvent
		}
	} = useSelector((state: EventsState) => state);
	const { colorOverride, onOverrideChange, defaultColor, elementName, tagType = "h1", value, disabled } = props;

	const colorPalette = workingEvent?.settings.design.colors.colors;
	const color = colorOverride?.split("-")[0]; //removing the "-color" from the classname
	const [text, setText] = useState(value);

	const [colorsOpen, setColorsOpen] = useState(false);

	// keep in sync with parent
	useEffect(() => {
		setText(value);
	}, [value]);

	// Use Refcallback per https://github.com/lovasoa/react-contenteditable/issues/161#issuecomment-736053428
	const handleTextChange = useRefCallback((event: any) => {
		setText(event?.target.value);
	}, []);

	const editorRef = useRef<HTMLDivElement | null>(null);

	// Use Refcallback per https://github.com/lovasoa/react-contenteditable/issues/161#issuecomment-736053428
	const handleBlur = useRefCallback(() => {
		if (convertFromHtml(text) !== value) { // Only update if changed
			props.onChange(convertFromHtml(text));
		}
	}, [text]);

	function handleColorChange(color: string) {
		if (!onOverrideChange) return;
		onOverrideChange({
			color
		});
	}

	const startingColor = color || defaultColor;
	const currentColor: [string, number] | undefined = startingColor ? colorPalette?.[startingColor] : ['#444', 1];

	return (
		<div
			style={props.style ?? {}}
			className={classNames("editable-text slim-container", { disabled }, props.className)}
		>
			<ContentEditable
				innerRef={editorRef}
				html={text || ''}
				onBlur={handleBlur}
				onChange={handleTextChange}
				tagName={tagType}
				className={classNames(colorOverride)}
			/>

			<button
				className="round edit-text-button"
				onClick={() => editorRef.current?.focus()}
				disabled={disabled}
			>
				<Icon name={ICONS.EDIT} size={12} color={COLORS.WHITE} />
			</button>
			{startingColor ? (
				<div className="toolbar slim">
					<button
						className={classNames("small round color-display")}
						style={{
							backgroundColor: currentColor ? parseColor(currentColor) : "",
						}}
						onClick={() => setColorsOpen(true)}
					/>
				</div>
			) : null}

			<EditSectionColorsModal
				open={colorsOpen}
				onClose={() => setColorsOpen(false)}
				onFinish={handleColorChange}
				title={elementName || EContentColorOverrideNames.title}
			/>
		</div >
	);
};

export default EditableTextSlim;
